// MCP prompts as slash commands.
//
// assistant-ui already has this: unstable_useSlashCommandAdapter turns a list
// of commands into the adapter and action that ComposerPrimitive's trigger
// popover takes, and the popover handles the "/" detection, the filtering as
// you type, keyboard navigation and the highlight. So none of that is here.
// This file only fetches the list and shapes it.
//
// Slash commands rather than ThreadSuggestion, which is the other thing
// assistant-ui offers: a suggestion is {title, label, prompt} where prompt IS
// the message text sent, so there is nowhere to put a prompt's arguments, and
// suggestions only show on an empty thread. A prompt has to be reachable
// mid-conversation, which is what "/" is for, and it is how every other MCP
// client surfaces them.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_useSlashCommandAdapter } from '@assistant-ui/react';

import { listBotPrompts, renderBotPrompt } from '../../services/prompts';

// useMcpPrompts fetches the bot's prompts, offers them as slash commands, and
// renders the one that gets picked.
//
// onText receives the rendered prompt and decides where it goes. Kept out here
// because the composer is the caller's, not this hook's: it holds the aui
// handle and knows what else is in the box.
export function useMcpPrompts({ project, bot, onText }) {
  const [sets, setSets] = useState([]);
  // The prompt waiting for its arguments, or null. One at a time: picking a
  // second command while a form is open replaces it, which is what somebody
  // who just hit the wrong one expects.
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // What the last render did to the prompt, when it did anything worth saying.
  // Cleared on the next pick, so it always describes what is in the composer.
  const [note, setNote] = useState('');

  useEffect(() => {
    // A bot switch must not leave the previous bot's commands in the menu,
    // which would offer prompts from servers this bot cannot reach.
    setSets([]);

    if (!project || !bot) return undefined;

    let live = true;

    listBotPrompts(project, bot)
      .then((fetched) => {
        if (!live) return;

        setSets(fetched);

        // Said out loud, because the menu is hidden when there is nothing to
        // show: without this, "the bot has no prompts" and "the request never
        // returned" are both just a slash key that does nothing.
        console.debug(
          `[prompts] ${project}/${bot}: ${fetched.length} server(s)`,
          fetched.map((set) => `${set.imcp} (${(set.prompts || []).length})`),
        );
      })
      .catch((err) => {
        // Still degrades to no commands, because a broken prompt list must not
        // stop somebody typing. But it is logged: swallowing this was what
        // made a silent failure indistinguishable from a bot that simply has
        // no prompts.
        if (live) setSets([]);

        console.warn(`[prompts] ${project}/${bot} could not be listed:`, err);
      });

    return () => {
      live = false;
    };
  }, [project, bot]);

  // Bumped whenever the answer would land somewhere it does not belong: a
  // different chat, or after somebody gave up on the form. A render is a round
  // trip to somebody else's MCP server, so it easily outlives the moment it
  // was asked for.
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
  }, [project, bot]);

  const render = useCallback(
    async (imcp, prompt, args) => {
      const started = generation.current;

      setBusy(true);
      setError('');

      try {
        const rendered = await renderBotPrompt(project, bot, imcp, prompt.name, args);

        // The chat moved on. Without this, picking a prompt on one bot and
        // switching before it resolved appended the first bot's text into the
        // second one's composer.
        if (generation.current !== started) return;

        if (rendered?.text) {
          onText?.(rendered);
        }

        setNote(renderNote(rendered));
        setPending(null);
      } catch (err) {
        if (generation.current !== started) return;

        // Kept on screen rather than thrown away: the render talks to somebody
        // else's MCP server, so "it wants an argument you did not give" and
        // "your account is not linked" both arrive here and are both worth
        // reading.
        setError(err.message || 'the prompt could not be rendered');
      } finally {
        if (generation.current === started) setBusy(false);
      }
    },
    [project, bot, onText],
  );

  // Arguments mean a form, even when none of them is required: an optional
  // argument nobody can fill is not optional, it is unreachable. Their
  // descriptions say things like "Empty = the most recent result", which is
  // only a default if there was a chance to type something else.
  const select = useCallback(
    ({ imcp, prompt }) => {
      setError('');
      setNote('');

      if ((prompt.arguments || []).length === 0) {
        render(imcp, prompt, {});

        return;
      }

      setPending({ imcp, prompt });
    },
    [render],
  );

  const commands = useMemo(() => promptCommands(sets, select), [sets, select]);

  const slash = unstable_useSlashCommandAdapter({
    commands,
    // True, or the library inserts a DIRECTIVE instead of clearing the
    // trigger: with false, triggerSelectionResource falls through to
    // insertDirective() and the composer ends up holding
    // ":command[Draw bar chart]{name=graph-local:draw-bar}" as literal text in
    // the message. True strips "/draw-bar" and puts the caret where it was, so
    // onExecute starts from a clean composer and can insert the rendered
    // prompt there.
    removeOnExecute: true,
  });

  return {
    ...slash,
    sets,
    hasPrompts: commands.length > 0,
    pending,
    busy,
    error,
    note,
    dismissNote: () => setNote(''),
    submit: (args) => pending && render(pending.imcp, pending.prompt, args),
    cancel: () => {
      // Counts as moving on: a render still in flight must not reopen the form
      // or drop its text in after somebody dismissed it.
      generation.current += 1;

      setPending(null);
      setError('');
      setNote('');
      setBusy(false);
    },
  };
}

// promptCommands flattens the per-server sets into one command list.
//
// Flat rather than nested by server, because the adapter takes a flat list and
// the popover filters it as you type: somebody typing "/triage" wants the
// prompt, not to pick a server first. The server rides in the description
// instead, which is also what tells two prompts of the same name apart.
function promptCommands(sets, onSelect) {
  const commands = [];

  for (const set of sets) {
    for (const prompt of set.prompts || []) {
      if (!prompt?.name) continue;

      commands.push({
        // Qualified, because prompt names are only unique within a server and
        // two MCPs offering "triage" is entirely ordinary.
        id: `${set.imcp}:${prompt.name}`,
        label: prompt.title || prompt.name,
        description: describe(set.imcp, prompt),
        // Rides metadata.icon, which is the one field besides id, label and
        // description that the slash adapter carries through to a trigger
        // item. It expects a key into an iconMap of components; we put the
        // data uri there and render it ourselves.
        icon: dataIcon(prompt.icons),
        execute: () => onSelect?.({ imcp: set.imcp, prompt }),
      });
    }
  }

  return commands;
}

// describe is the one line under a command. The server name is always in it:
// the same prompt name from two servers is otherwise indistinguishable, and
// which backend a prompt talks to is the thing an operator cares about.
function describe(imcp, prompt) {
  const parts = [imcp];

  if (prompt.description) parts.push(prompt.description);

  const required = (prompt.arguments || []).filter((arg) => arg?.required);
  if (required.length > 0) {
    parts.push(`needs ${required.map((arg) => arg.name).join(', ')}`);
  }

  return parts.join(' · ');
}

// renderNote is what to tell somebody about the text that just landed in their
// composer, when there is anything to tell.
//
// Both cases are the same kind of thing: what the server rendered is not
// exactly what is about to be sent, and the person sending it should know
// before they press enter rather than after.
function renderNote(rendered) {
  const notes = [];

  if (rendered?.flattened) {
    notes.push(
      'This prompt is a conversation, not a message. The turns were joined ' +
        'with their roles labelled so you can edit them, which means the model ' +
        'reads them as a quoted exchange rather than as its own earlier reply.',
    );
  }

  if (rendered?.dropped > 0) {
    const what = rendered.dropped === 1 ? 'part' : 'parts';
    notes.push(
      `${rendered.dropped} ${what} of this prompt were images or binary data ` +
        'and were left out: only text is ever sent to the model.',
    );
  }

  return notes.join(' ');
}

// dataIcon is the icon we can actually draw: a base64 data uri.
//
// MCP allows an icon's src to be an http(s) url or a data uri. Only the second
// is usable here. Installations are frequently air-gapped, so a remote url
// would simply not load, and where it did load it would fire a request to a
// server the MCP chose every time somebody opened the menu, from a page that
// otherwise talks only to us. Ignoring them costs a picture and settles both.
//
// An svg data uri is fine in the <img> this ends up in: browsers disable
// script for image-sourced svg, which matters because the spec suggests svg
// for icons declaring sizes ["any"].
function dataIcon(icons) {
  for (const icon of icons || []) {
    const src = icon?.src || '';

    if (!src.startsWith('data:')) continue;
    // The chat is light-backgrounded, so an icon drawn for a dark one would
    // disappear into it. Untagged icons are meant for either.
    if (icon.theme === 'dark') continue;

    return src;
  }

  return undefined;
}
